import * as R from 'ramda'
import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  webStorage,
  makeQueryParams,
} from 'sidecar-utility-library'

import { initialState, types } from './types'
import { types as applicantTypes } from 'store/Applicant'

const isPrimary = R.propEq('primary', true)

export const fetchMembers = () => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `accounts/${webStorage.getItem('aud')}/members`,
    ),
    method: 'GET',
    types: [
      types.FETCH_MEMBERS_REQUEST,
      types.FETCH_MEMBERS_SUCCESS,
      types.FETCH_MEMBERS_ERROR,
    ],
  },
})

export const fetchAuthedUser = () => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `members/${webStorage.getItem('sub')}`),
    method: 'GET',
    types: [
      types.FETCH_AUTHED_USER_REQUEST,
      {
        type: types.FETCH_AUTHED_USER_SUCCESS,
        meta: { uuid: webStorage.getItem('sub') },
      },
      types.FETCH_AUTHED_USER_ERROR,
    ],
  },
})

export const updateMember = (uuid, updatedMember) => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `members/${uuid}`),
    method: 'POST',
    body: JSON.stringify(updatedMember),
    types: [
      types.UPDATE_MEMBER_REQUEST,
      types.UPDATE_MEMBER_SUCCESS,
      types.UPDATE_MEMBER_ERROR,
    ],
  },
})

export const reinstateMember = uuid => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `members/${uuid}/reinstate`),
    method: 'POST',
    body: JSON.stringify({ uuid }),
    types: [
      types.REINSTATE_MEMBER_REQUEST,
      types.REINSTATE_MEMBER_SUCCESS,
      types.REINSTATE_MEMBER_ERROR,
    ],
  },
})

export const decideOnExclusions = (uuid, dto) => ({
  [RSAA]: {
    endpoint: getApiPath('acct', `members/${uuid}/decideExclusion`),
    method: 'POST',
    body: JSON.stringify(dto),
    types: [
      types.DECIDE_ON_EXCLUSIONS_REQUEST,
      types.DECIDE_ON_EXCLUSIONS_SUCCESS,
      types.DECIDE_ON_EXCLUSIONS_ERROR,
    ],
  },
})

export const undoCancellation = uuid => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `members/${uuid}/undoCancel`),
    method: 'POST',
    types: [
      types.UNDO_CANCELLATION_REQUEST,
      types.UNDO_CANCELLATION_SUCCESS,
      types.UNDO_CANCELLATION_ERROR,
    ],
  },
})

export const assignOwner = owner => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `account/${webStorage.getItem('aud')}/assign-owner`,
    ),
    method: 'POST',
    body: JSON.stringify(owner),
    types: [
      types.ASSIGN_OWNER_REQUEST,
      types.ASSIGN_OWNER_SUCCESS,
      types.ASSIGN_OWNER_ERROR,
    ],
  },
})

export const fetchMemberServiceLimits = (memberUuid, query) => {
  const sanitizedQuery = query || ''
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `policy/servicelimit/account/${webStorage.getItem(
          'aud',
        )}/member/${memberUuid}${sanitizedQuery}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_MEMBER_SERVICE_LIMITS_REQUEST,
        types.FETCH_MEMBER_SERVICE_LIMITS_SUCCESS,
        types.FETCH_MEMBER_SERVICE_LIMITS_ERROR,
      ],
    },
  }
}

export const fetchServiceLimitDetails = ({
  memberUuid,
  serviceLimitUuid,
  query = '',
}) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `members/${memberUuid}/servicelimits/${serviceLimitUuid}/expense-dtls${query}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_SERVICE_LIMIT_DETAILS_REQUEST,
      types.FETCH_SERVICE_LIMIT_DETAILS_SUCCESS,
      types.FETCH_SERVICE_LIMIT_DETAILS_ERROR,
    ],
  },
})

export const fetchServiceLimitDates = (memberUuid, serviceLimitUuid, query) => {
  const sanitizedQuery = query || ''
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `members/${memberUuid}/servicelimits/${serviceLimitUuid}/dates${sanitizedQuery}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_SERVICE_LIMIT_DATES_REQUEST,
        types.FETCH_SERVICE_LIMIT_DATES_SUCCESS,
        types.FETCH_SERVICE_LIMIT_DATES_ERROR,
      ],
    },
  }
}

// API Call Calendar year accruables per MEMBER
export const fetchAccruables = (
  memberUuid,
  benefitType,
  startDate,
  endDate,
) => {
  const queryParams = { localStartDate: startDate, localEndDate: endDate }
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `members/${memberUuid}/benefits/${benefitType}/expense-details${makeQueryParams(
          queryParams,
        )}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_ACCRUABLES_REQUEST,
        types.FETCH_ACCRUABLES_SUCCESS,
        types.FETCH_ACCRUABLES_ERROR,
      ],
    },
  }
}

// API Call for benefit details per MEMBER
export const fetchBenefitDetails = (
  memberUuid,
  benefitType,
  startDate,
  endDate,
) => {
  const queryParams = { startDate, endDate }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `members/${memberUuid}/benefits/${benefitType}/details${makeQueryParams(
          queryParams,
        )}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_BENEFIT_DETAILS_REQUEST,
        types.FETCH_BENEFIT_DETAILS_SUCCESS,
        types.FETCH_BENEFIT_DETAILS_ERROR,
      ],
    },
  }
}

export const fetchAccruableDates = memberUuid => ({
  [RSAA]: {
    endpoint: getApiPath('plcy', `members/${memberUuid}/accrual-periods`),
    method: 'GET',
    types: [
      types.FETCH_ACCRUABLE_DATES_REQUEST,
      types.FETCH_ACCRUABLE_DATES_SUCCESS,
      types.FETCH_ACCRUABLE_DATES_ERROR,
    ],
  },
})

/**
 * Updates the member's PHI communication preference.
 *
 * @param {string} memberUuid - The member's UUID
 * @param {boolean} phiCommunicationEnabled - Whether to include health details
 *   in communications
 */
export const updatePhiCommunicationPreference = (
  memberUuid,
  phiCommunicationEnabled,
) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `members/${memberUuid}/phi-communication-preference`,
    ),
    method: 'POST',
    body: JSON.stringify({
      phiCommunicationEnabled,
      changeType: 'REGISTRATION',
    }),
    types: [
      types.UPDATE_PHI_COMMUNICATION_PREFERENCE_REQUEST,
      types.UPDATE_PHI_COMMUNICATION_PREFERENCE_SUCCESS,
      types.UPDATE_PHI_COMMUNICATION_PREFERENCE_ERROR,
    ],
  },
})

/**
 * Sets the selected member UUID in Redux state. Used to persist member
 * selection across navigation (e.g., Dashboard -> Care -> Dashboard).
 *
 * @param {string} memberUuid - The UUID of the selected member
 */
export const setSelectedMemberUuid = memberUuid => ({
  type: types.SET_SELECTED_MEMBER_UUID,
  payload: memberUuid,
})

const membersRequestHandler = state => ({
  ...state,
  members: { ...state.members, busy: true, error: '' },
})

const membersErrorHandler = (state, payload) => {
  const message = R.pathOr(
    'Something went wrong',
    ['response', 'message'],
    payload,
  )
  return {
    ...state,
    members: { ...state.members, busy: false, error: message },
  }
}

let MEMBER_REDUCER_LOOKUP = null
const getMemberReducerLookup = () => {
  if (!MEMBER_REDUCER_LOOKUP) {
    MEMBER_REDUCER_LOOKUP = {
      [types.DECIDE_ON_EXCLUSIONS_REQUEST]: membersRequestHandler,
      [types.FETCH_MEMBERS_REQUEST]: membersRequestHandler,
      [types.REINSTATE_MEMBER_REQUEST]: membersRequestHandler,
      [types.UNDO_CANCELLATION_REQUEST]: membersRequestHandler,
      [types.UPDATE_MEMBER_REQUEST]: membersRequestHandler,
      [types.ASSIGN_OWNER_REQUEST]: membersRequestHandler,

      [types.DECIDE_ON_EXCLUSIONS_ERROR]: membersErrorHandler,
      [types.FETCH_MEMBERS_ERROR]: membersErrorHandler,
      [types.REINSTATE_MEMBER_ERROR]: membersErrorHandler,
      [types.UNDO_CANCELLATION_ERROR]: membersErrorHandler,
      [types.UPDATE_MEMBER_ERROR]: membersErrorHandler,
      [types.ASSIGN_OWNER_ERROR]: membersErrorHandler,

      // ----------
      [types.REINSTATE_MEMBER_SUCCESS]: (state, payload) => {
        const newMembers = {
          ...state.members.entities,
          [payload.uuid]: {
            ...state.members.entities[payload.uuid],
            ...payload,
          },
        }
        return {
          ...state,
          dependents: R.reject(isPrimary, Object.values(newMembers)),
          primaryMember: R.find(isPrimary, Object.values(newMembers)),
          members: { ...state.members, busy: false, entities: newMembers },
        }
      },
      [types.UPDATE_MEMBER_SUCCESS]: (state, payload) => {
        const newMembers = {
          ...state.members.entities,
          [payload.uuid]: {
            ...state.members.entities[payload.uuid],
            ...payload,
          },
        }
        return {
          ...state,
          dependents: R.reject(isPrimary, Object.values(newMembers)),
          primaryMember: R.find(isPrimary, Object.values(newMembers)),
          members: { ...state.members, busy: false, entities: newMembers },
        }
      },

      // ----------
      [types.FETCH_MEMBERS_SUCCESS]: (state, payload) => ({
        ...state,
        dependents: R.reject(isPrimary, payload),
        primaryMember: R.find(isPrimary, payload),
        members: {
          ...state.members,
          busy: false,
          entities: payload.reduce((obj, member) => {
            obj[member.uuid] = member
            return obj
          }, {}),
        },
      }),

      // ----------
      // For the authedUser stuff we want to basically do the same as
      // we're doing for all the other generic member state stuff, but
      // we're also going to be making sure that we update the specific
      // authedUserBusy flag too.
      [types.FETCH_AUTHED_USER_REQUEST]: state => ({
        ...state,
        authedUserBusy: true,
        members: { ...state.members, busy: true, error: '' },
      }),
      [types.FETCH_AUTHED_USER_SUCCESS]: (state, payload, meta) => {
        const { uuid } = meta
        const newMembers = {
          ...state.members.entities,
          [uuid]: R.mergeDeepRight(
            state?.members?.entities[uuid] || {},
            payload,
          ),
        }
        return {
          ...state,
          authedUserBusy: false,
          dependents: R.reject(isPrimary, Object.values(newMembers)),
          primaryMember: R.find(isPrimary, Object.values(newMembers)),
          members: { ...state.members, busy: false, entities: newMembers },
        }
      },
      [types.FETCH_AUTHED_USER_ERROR]: (state, payload) => {
        const message = R.pathOr(
          'Something went wrong',
          ['response', 'message'],
          payload,
        )
        return {
          ...state,
          authedUserBusy: false,
          members: { ...state.members, busy: false, error: message },
        }
      },

      // ----------
      [types.DECIDE_ON_EXCLUSIONS_SUCCESS]: (state, payload) => {
        // this needs to be replaceOrCreate not updateOrCreate because this response
        // will remove the pendingChange object from the policy... we
        // don't want it lingering around in the member's policy anymore
        const newMembers = {
          ...state.members.entities,
          [payload.uuid]: payload,
        }
        return {
          ...state,
          dependents: R.reject(isPrimary, Object.values(newMembers)),
          primaryMember: R.find(isPrimary, Object.values(newMembers)),
          members: { ...state.members, busy: false, entities: newMembers },
        }
      },

      // ----------
      // API returns a 204 No Content, just need to untoggle busy flag
      [types.UNDO_CANCELLATION_SUCCESS]: state => ({
        ...state,
        members: { ...state.members, busy: false },
      }),

      // ----------
      [applicantTypes.DELETE_APPLICANT_SUCCESS]: (state, payload, meta) => {
        const newMembers = R.filter(
          !R.propEq('uuid', meta.applicantUuid),
          state.members.entities,
        )
        return {
          ...state,
          dependents: R.reject(isPrimary, Object.values(newMembers)),
          primaryMember: R.find(isPrimary, Object.values(newMembers)),
          members: { ...state.members, entities: newMembers },
        }
      },

      // ----------
      [types.ASSIGN_OWNER_SUCCESS]: (state, payload) => ({
        ...state,
        primaryMember: { ...state.primaryMember, ...payload },
        members: { ...state.members, busy: false },
      }),

      // ----------
      [types.FETCH_MEMBER_SERVICE_LIMITS_REQUEST]: state => ({
        ...state,
        memberServiceLimits: {
          ...state.memberServiceLimits,
          busy: true,
          error: null,
        },
      }),
      [types.FETCH_MEMBER_SERVICE_LIMITS_SUCCESS]: (state, payload) => ({
        ...state,
        memberServiceLimits: {
          ...state.memberServiceLimits,
          busy: false,
          entity: payload,
        },
      }),
      [types.FETCH_MEMBER_SERVICE_LIMITS_ERROR]: (state, payload) => ({
        ...state,
        memberServiceLimits: {
          ...state.memberServiceLimits,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // --------------------------
      [types.FETCH_SERVICE_LIMIT_DETAILS_REQUEST]: state => ({
        ...state,
        serviceLimitDetails: {
          ...state.serviceLimitDetails,
          busy: true,
          error: null,
        },
      }),
      [types.FETCH_SERVICE_LIMIT_DETAILS_SUCCESS]: (state, payload) => ({
        ...state,
        serviceLimitDetails: {
          ...state.serviceLimitDetails,
          busy: false,
          entity: payload,
        },
      }),
      [types.FETCH_SERVICE_LIMIT_DETAILS_ERROR]: (state, payload) => ({
        ...state,
        serviceLimitDetails: {
          ...state.serviceLimitDetails,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // --------------------------
      [types.FETCH_SERVICE_LIMIT_DATES_REQUEST]: state => ({
        ...state,
        serviceLimitDates: {
          ...state.serviceLimitDates,
          busy: true,
          error: null,
        },
      }),
      [types.FETCH_SERVICE_LIMIT_DATES_SUCCESS]: (state, payload) => ({
        ...state,
        serviceLimitDates: {
          ...state.serviceLimitDates,
          busy: false,
          entities: payload,
        },
      }),
      [types.FETCH_SERVICE_LIMIT_DATES_ERROR]: (state, payload) => ({
        ...state,
        serviceLimitDates: {
          ...state.serviceLimitDates,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // --------------------------
      // Calendar year accruables
      [types.FETCH_ACCRUABLES_REQUEST]: state => ({
        ...state,
        accruables: { ...state.accruables, busy: true, error: null },
      }),
      [types.FETCH_ACCRUABLES_SUCCESS]: (state, payload) => ({
        ...state,
        accruables: { ...state.accruables, busy: false, entity: payload },
      }),
      [types.FETCH_ACCRUABLES_ERROR]: (state, payload) => ({
        ...state,
        accruables: {
          ...state.accruables,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // Benefit details
      [types.FETCH_BENEFIT_DETAILS_REQUEST]: state => ({
        ...state,
        accruables: { ...state.accruables, busy: true, error: null },
      }),
      [types.FETCH_BENEFIT_DETAILS_SUCCESS]: (state, payload) => ({
        ...state,
        accruables: { ...state.accruables, busy: false, entity: payload },
      }),
      [types.FETCH_BENEFIT_DETAILS_ERROR]: (state, payload) => ({
        ...state,
        accruables: {
          ...state.accruables,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // Calendar year accruables dates
      [types.FETCH_ACCRUABLE_DATES_REQUEST]: state => ({
        ...state,
        accruableDates: { ...state.accruableDates, busy: true, error: null },
      }),
      [types.FETCH_ACCRUABLE_DATES_SUCCESS]: (state, payload) => ({
        ...state,
        accruableDates: {
          ...state.accruableDates,
          busy: false,
          entity: payload,
        },
      }),
      [types.FETCH_ACCRUABLE_DATES_ERROR]: (state, payload) => ({
        ...state,
        accruableDates: {
          ...state.accruableDates,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // --------------------------
      // PHI Communication Preference
      [types.UPDATE_PHI_COMMUNICATION_PREFERENCE_REQUEST]: state => ({
        ...state,
        phiCommunicationPreference: {
          ...state.phiCommunicationPreference,
          busy: true,
          error: null,
        },
      }),
      [types.UPDATE_PHI_COMMUNICATION_PREFERENCE_SUCCESS]: state => ({
        ...state,
        phiCommunicationPreference: {
          ...state.phiCommunicationPreference,
          busy: false,
          error: null,
        },
      }),
      [types.UPDATE_PHI_COMMUNICATION_PREFERENCE_ERROR]: (state, payload) => ({
        ...state,
        phiCommunicationPreference: {
          ...state.phiCommunicationPreference,
          busy: false,
          error: R.pathOr(
            'Something went wrong',
            ['response', 'message'],
            payload,
          ),
        },
      }),

      // --------------------------
      // Selected Member UUID
      [types.SET_SELECTED_MEMBER_UUID]: (state, payload) => ({
        ...state,
        selectedMemberUuid: payload,
      }),
    }
  }
  return MEMBER_REDUCER_LOOKUP
}

export default (state = initialState, { type, payload = {}, meta = {} }) =>
  getMemberReducerLookup()[type]?.(state, payload, meta) ?? state
